import {createContext, useContext, useMemo} from 'react';
import {
  createObjectIntentType,
  getIntents,
  toIntent,
  provider,
} from '../intent';
import {onRender} from './intentTypes';
import {deprecatedCss, getCascadingProperty} from './deprecatedCss';
import type {Intent} from '../intent';

const $css = Symbol('css');
const $raw = Symbol('raw');

type GlobalValue = string | number;

type ResolvedVars<T = GlobalValue> = Record<string, T> & {
  [$css]: Intent;
  [$raw]: GlobalVars<T>;
};

export type GlobalVars<T = GlobalValue> = {
  [key: string]: T | ((vars: ResolvedVars<T>) => T);
};

const globalVarsIntent = createObjectIntentType(
  'globalVars',
  (createIntent) =>
    <T extends GlobalValue = GlobalValue>(vars: GlobalVars<T>) =>
      createIntent(vars),
);

function toDecls(resolved: ResolvedVars): string {
  // TODO(koop): Only regenerate CSS for properties that have changed or
  // whose dependencies have changed. Currently regenerates all properties.
  let decls = '';
  Object.keys(resolved).forEach((key) => {
    decls += `${getCascadingProperty(key)}: ${resolved[key]};\n`;
  });
  return decls;
}

export function resolveGlobalVars(
  child: GlobalVars,
  parent?: ResolvedVars,
): ResolvedVars {
  const raw = parent ? {...parent[$raw], ...child} : child;

  // Proxy the raw variables so accessing a value lazily computes the value and
  // its dependencies.
  const proxy = new Proxy({[$raw]: raw} as ResolvedVars, {
    get(target, key: string) {
      if (!(key in target) && key in raw) {
        const value = raw[key];
        target[key] = typeof value === 'function' ? value(proxy) : value;
      }
      return target[key];
    },
    getOwnPropertyDescriptor(_target, key) {
      return Reflect.getOwnPropertyDescriptor(raw, key);
    },
    ownKeys() {
      return Reflect.ownKeys(raw);
    },
  });

  proxy[$css] = deprecatedCss(toDecls(proxy));

  return proxy;
}

const GlobalVarsContext = createContext<ResolvedVars | undefined>(undefined);

const EMPTY_VARS = {[$raw]: {}, [$css]: {} as Intent} as ResolvedVars;

export function useGlobalVars(): ResolvedVars {
  return useContext(GlobalVarsContext) || EMPTY_VARS;
}

export function useGlobalVarsCss(): Intent {
  return useGlobalVars()[$css];
}

export const globalVarsCss = onRender(() => [useGlobalVarsCss()]);

export function useGlobalVar<T extends GlobalValue = GlobalValue>(
  key: string,
): T {
  return useGlobalVars()[key] as T;
}

const processGlobalVars = onRender((props) => {
  const parent = useContext(GlobalVarsContext);
  const vars = getIntents(globalVarsIntent, props);
  return useMemo(() => {
    const resolved = resolveGlobalVars(vars as unknown as GlobalVars, parent);
    return [provider(GlobalVarsContext, resolved), resolved[$css]];
  }, [parent, vars]);
});

export function deprecatedGlobalVars<T extends GlobalValue = GlobalValue>(
  vars: GlobalVars<T>,
): Intent {
  return toIntent([globalVarsIntent(vars), processGlobalVars]);
}
